package com.flowyride.feature.account

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.flowyride.core.domain.account.AccountException
import com.flowyride.core.domain.account.AccountRepository
import com.flowyride.core.domain.account.AccountRules
import com.flowyride.feature.account.resources.Res
import com.flowyride.feature.account.resources.error_password_short
import com.flowyride.feature.account.resources.error_wrong_current_password
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.StringResource

data class ChangePasswordUiState(
    val currentPassword: String = "",
    val newPassword: String = "",
    val busy: Boolean = false,
    val error: StringResource? = null,
    val done: Boolean = false,
)

sealed interface ChangePasswordAction {
    data class CurrentChanged(val value: String) : ChangePasswordAction
    data class NewChanged(val value: String) : ChangePasswordAction
    data object Submit : ChangePasswordAction
    /** The dialog was closed: start blank next time. */
    data object Reset : ChangePasswordAction
}

/** Typically right after an admin handed over a temporary password. */
class ChangePasswordViewModel(private val accounts: AccountRepository) : ViewModel() {

    private val _state = MutableStateFlow(ChangePasswordUiState())
    val state: StateFlow<ChangePasswordUiState> = _state.asStateFlow()

    fun onAction(action: ChangePasswordAction) {
        when (action) {
            is ChangePasswordAction.CurrentChanged -> _state.update { it.copy(currentPassword = action.value, error = null) }
            is ChangePasswordAction.NewChanged -> _state.update { it.copy(newPassword = action.value, error = null) }
            ChangePasswordAction.Submit -> submit()
            ChangePasswordAction.Reset -> _state.value = ChangePasswordUiState()
        }
    }

    private fun submit() {
        val current = _state.value
        if (current.busy) return
        if (!AccountRules.isValidPassword(current.newPassword)) {
            _state.update { it.copy(error = Res.string.error_password_short) }
            return
        }
        _state.update { it.copy(busy = true, error = null) }
        viewModelScope.launch {
            try {
                accounts.changePassword(current.currentPassword, current.newPassword)
                _state.update { ChangePasswordUiState(done = true) }
            } catch (e: AccountException) {
                val message = if (e is AccountException.InvalidCredentials) Res.string.error_wrong_current_password else e.toMessage()
                _state.update { it.copy(busy = false, error = message) }
            }
        }
    }
}
